import {
  ReactNode,
  useEffect,
  useRef,
  ClipboardEvent,
  DragEvent,
  useState,
  useCallback,
} from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { RecordButton } from '@backrs_ui/common-ui/src/atoms/RecordButton';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import {
  AudioRecorderErrorReason,
  useAudioRecorder,
} from '@backrs_ui/common-ui/src/hooks/useAudioRecorder';
import { ContentNugget } from '@backrs_ui/common-ui/src/models/nugget';
import { AttachedFileChip } from '@backrs_ui/common-ui/src/molecules/Lubav/AttachedFileChip';

import { Box, Button, IconButton, SxProps, Theme, useMediaQuery } from '@mui/material';
import TextareaAutosize, { TextareaAutosizeProps } from 'react-textarea-autosize';

const INPUT_BOX_STRINGS = appStrings.modules.lubav.inputBox;
const RECORDING_ERRORS = appStrings.modules.audioRecording.errors;

export const NUGGET_DRAG_TYPE = 'application/x-backrs-nugget';

export type AttachedNugget = Readonly<{
  id: string;
  type: string;
  title: string;
  contentNugget?: ContentNugget;
}>;

export type ChatAttachment = Readonly<{
  s3_key: string;
  filename: string;
  mime_type: string;
  uploadStatus?: 'uploading' | 'done' | 'error';
}>;

const CHAT_ATTACHMENT_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
]);

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const MAX_RECORDING_MS = 2 * 60 * 1000;

export type InputBoxProps = Readonly<{
  value?: string;
  onChange?: (value: string) => void;
  onSend?: () => void;
  onStop?: () => void;
  isNumeric?: boolean;
  placeholder?: string;
  disabled?: boolean;
  sendButtonDisabled?: boolean;
  isStreaming?: boolean;
  readOnly?: boolean;

  // Nugget attachment
  attachedNuggets?: readonly AttachedNugget[];
  onNuggetDrop?: (nugget: AttachedNugget) => void;
  onNuggetRemove?: (id: string) => void;
  renderAttachedNugget?: (nugget: AttachedNugget, onRemove: () => void) => ReactNode;

  // File attachment
  attachedFiles?: readonly ChatAttachment[];
  onFileSelect?: (files: File[]) => void;
  onFileRemove?: (s3Key: string) => void;
  onFileValidationError?: (message: string) => void;

  // Voice input
  onTranscribe?: (audio: Blob) => Promise<string>;
  onRecordingError?: (message: string) => void;
}>;

const partitionValidFiles = (files: readonly File[]) => {
  const valid: File[] = [];
  let rejected = 0;

  for (const file of files) {
    if (CHAT_ATTACHMENT_MIME_TYPES.has(file.type) && file.size <= MAX_FILE_SIZE) {
      valid.push(file);
    } else {
      rejected += 1;
    }
  }

  return { valid, rejected };
};

// Pasted screenshots sometimes arrive without a filename, which would leave the
// attachment chip blank.
const withPasteFilename = (file: File) => {
  if (file.name) return file;

  const extension = file.type.split('/')[1];

  return new File([file], extension ? `pasted-file.${extension}` : 'pasted-file', {
    type: file.type,
  });
};

const noTranscription = async () => '';

export const InputBox = ({
  value = '',
  onChange,
  onSend,
  onStop,
  isNumeric = false,
  placeholder = undefined,
  disabled = false,
  sendButtonDisabled = false,
  isStreaming = false,
  readOnly = false,
  attachedNuggets = [],
  onNuggetDrop,
  onNuggetRemove,
  renderAttachedNugget,
  attachedFiles = [],
  onFileSelect,
  onFileRemove,
  onFileValidationError,
  onTranscribe,
  onRecordingError,
}: InputBoxProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useMediaQuery('(max-width: 900px)');
  const [isDragOver, setIsDragOver] = useState(false);

  // The transcript arrives after an await, so appending must use whatever the user
  // has typed by then rather than the captured render.
  const valueRef = useRef(value);

  valueRef.current = value;

  const handleTranscript = useCallback(
    (text: string) => {
      const existing = valueRef.current.trimEnd();

      onChange?.(existing ? `${existing} ${text}` : text);
    },
    [onChange],
  );

  const handleRecordingError = useCallback(
    (reason: AudioRecorderErrorReason) => {
      onRecordingError?.(RECORDING_ERRORS[reason]);
    },
    [onRecordingError],
  );

  const {
    status: recordingStatus,
    startRecording,
    stopRecording,
  } = useAudioRecorder({
    transcribe: onTranscribe ?? noTranscription,
    onTranscript: handleTranscript,
    onError: handleRecordingError,
    maxDurationMs: MAX_RECORDING_MS,
  });

  const isRecording = recordingStatus === 'recording';
  const isTranscribing = recordingStatus === 'transcribing';

  const handleInputChange = (newValue: string) => {
    // if isNumeric, we only allow numeric values
    if (isNumeric && isNaN(Number(newValue))) return;

    onChange?.(newValue);
  };

  const hasUploadingFiles = attachedFiles.some((f) => f.uploadStatus === 'uploading');
  const isSendBlocked =
    disabled || sendButtonDisabled || hasUploadingFiles || isRecording || isTranscribing;

  const handleSendMessage = () => {
    if (!value.trim() || isSendBlocked) return;

    onSend?.();
  };

  const handleStopStreaming = () => {
    onStop?.();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isSendBlocked) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const { valid, rejected } = partitionValidFiles(files);

    if (valid.length > 0) {
      onFileSelect?.(valid);
    }

    if (rejected > 0) {
      onFileValidationError?.(INPUT_BOX_STRINGS.fileValidationError);
    }

    // Reset the input so the same file can be selected again
    e.target.value = '';
  };

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      const hasNugget = event.dataTransfer.types.includes(NUGGET_DRAG_TYPE);
      const hasFiles = event.dataTransfer.types.includes('Files');

      if ((hasNugget && onNuggetDrop) || (hasFiles && onFileSelect)) {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        setIsDragOver(true);
      }
    },
    [onNuggetDrop, onFileSelect],
  );

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragOver(false);

      // Handle nugget drops
      const data = event.dataTransfer.getData(NUGGET_DRAG_TYPE);

      if (data && onNuggetDrop) {
        try {
          const nugget = JSON.parse(data) as AttachedNugget;

          onNuggetDrop(nugget);
        } catch {
          // Invalid data, ignore
        }

        return;
      }

      // Handle file drops
      if (event.dataTransfer.files.length > 0 && onFileSelect) {
        const { valid, rejected } = partitionValidFiles(Array.from(event.dataTransfer.files));

        if (valid.length > 0) {
          onFileSelect(valid);
        }

        if (rejected > 0) {
          onFileValidationError?.(INPUT_BOX_STRINGS.fileValidationError);
        }
      }
    },
    [onNuggetDrop, onFileSelect, onFileValidationError],
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      const files = Array.from(event.clipboardData.files);

      if (files.length === 0 || !onFileSelect) return;

      // Only claim the paste once we know it carries files, so text pastes are untouched
      event.preventDefault();

      const { valid, rejected } = partitionValidFiles(files.map(withPasteFilename));

      if (valid.length > 0) {
        onFileSelect(valid);
      }

      if (rejected > 0) {
        onFileValidationError?.(INPUT_BOX_STRINGS.fileValidationError);
      }
    },
    [onFileSelect, onFileValidationError],
  );

  // for auto focusing the input box when it is active
  useEffect(() => {
    if (!disabled && !isMobile) {
      textareaRef.current?.focus();
    }
  }, [disabled, isMobile]);

  const hasAttachedNuggets = attachedNuggets.length > 0;
  const hasAttachedFiles = attachedFiles.length > 0;

  // Must stay visible while active, or typing mid-recording would strand the
  // recording with no way to stop it.
  const showInlineRecordButton =
    Boolean(onTranscribe) && isMobile && !readOnly && (!value || isRecording || isTranscribing);

  const renderRecordButton = (id: string) => (
    <RecordButton
      id={id}
      status={recordingStatus}
      label={isRecording ? INPUT_BOX_STRINGS.stopRecording : INPUT_BOX_STRINGS.recordMessage}
      onStart={startRecording}
      onStop={stopRecording}
      disabled={disabled}
      sx={recordButtonSx}
    />
  );

  return (
    <Box sx={containerStyle(isMobile, readOnly)}>
      {onFileSelect && (
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.pdf,.docx,.txt"
          multiple
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
      )}
      <Box
        sx={boxStyle(isDragOver, isMobile)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {hasAttachedNuggets && renderAttachedNugget && (
          <Box sx={attachedNuggetsContainerSx}>
            {attachedNuggets.map((nugget) => (
              <Box key={`${nugget.type}-${nugget.id}`}>
                {renderAttachedNugget(nugget, () => onNuggetRemove?.(nugget.id))}
              </Box>
            ))}
          </Box>
        )}
        {hasAttachedFiles && (
          <Box sx={attachedNuggetsContainerSx}>
            {attachedFiles.map((file) => (
              <AttachedFileChip
                key={file.s3_key || file.filename}
                filename={file.filename}
                status={file.uploadStatus}
                onRemove={
                  onFileRemove && file.uploadStatus !== 'uploading'
                    ? () => onFileRemove(file.s3_key)
                    : undefined
                }
              />
            ))}
          </Box>
        )}
        <Box sx={textareaRowSx}>
          <Box sx={textareaWrapperSx}>
            <TextareaAutosize
              id="lubav-chat-textarea"
              ref={textareaRef}
              value={value}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyPress={handleKeyPress}
              onPaste={handlePaste}
              aria-placeholder={
                !disabled ? placeholder || INPUT_BOX_STRINGS.typeAMessage : undefined
              }
              style={messageInputTextarea(isMobile)}
              disabled={disabled || readOnly}
              minRows={1}
              maxRows={Math.floor((window.innerHeight * 0.2) / 24)}
            />
            {!value && !disabled && (
              <Box sx={placeholderOverlaySx(isMobile)}>
                {placeholder || INPUT_BOX_STRINGS.typeAMessage}
              </Box>
            )}
          </Box>
          {showInlineRecordButton && renderRecordButton('lubav-input-record-btn-mobile')}
        </Box>
        <Box sx={bottomRowSx}>
          {onFileSelect && !readOnly && !isMobile && (
            <IconButton
              id="lubav-input-attach-file-btn"
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              sx={attachButtonSx}
            >
              <AppIcon
                iconName="Paperclip"
                diameter="20px"
                color="Colors/Foreground/fg-quaternary"
              />
            </IconButton>
          )}
          {onTranscribe && !readOnly && !isMobile && renderRecordButton('lubav-input-record-btn')}
          <Box sx={{ flex: 1 }} />
          {!readOnly &&
            !isMobile &&
            (isStreaming ? (
              <Button
                id="lubav-input-stop-button-desktop"
                onClick={handleStopStreaming}
                hierarchy="Secondary gray"
                size="md"
                sx={stopButtonSx}
              >
                <AppIcon
                  iconName="Square"
                  diameter="12px"
                  color="Colors/Foreground/fg-quaternary"
                  fill="Colors/Foreground/fg-quaternary"
                />
                {INPUT_BOX_STRINGS.stop}
              </Button>
            ) : (
              <Button
                id="lubav-input-send-button-desktop"
                onClick={handleSendMessage}
                disabled={isSendBlocked}
                hierarchy="Primary"
                size="md"
              >
                {INPUT_BOX_STRINGS.send}
              </Button>
            ))}
        </Box>
      </Box>
      {!readOnly && isMobile && (
        <Box sx={mobileButtonsSx}>
          {onFileSelect && (
            <IconButton
              id="lubav-input-attach-file-btn-mobile"
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              sx={attachButtonSx}
            >
              <AppIcon
                iconName="Paperclip"
                diameter="20px"
                color="Colors/Foreground/fg-quaternary"
              />
            </IconButton>
          )}
          {isStreaming ? (
            <Button
              id="lubav-input-stop-button-mobile"
              onClick={handleStopStreaming}
              hierarchy="Secondary gray"
              size="md"
              sx={iconButtonStyle}
            >
              <AppIcon
                iconName="Square"
                diameter="20px"
                color="Colors/Foreground/fg-quaternary"
                fill="Colors/Foreground/fg-quaternary"
              />
            </Button>
          ) : (
            <Button
              id="lubav-input-send-button-mobile"
              onClick={handleSendMessage}
              disabled={isSendBlocked}
              hierarchy="Primary"
              size="md"
              sx={iconButtonStyle}
            >
              <AppIcon iconName="Send01" diameter="20px" color="Colors/Base/White" />
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

const containerStyle =
  (isMobile: boolean, readOnly: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    alignItems: isMobile ? 'center' : 'flex-end',
    gap: backrs.spacing['spacing-sm'],
    borderRadius: backrs.radius['radius-md'],
    pb: isMobile && !readOnly ? backrs.spacing['spacing-lg'] : undefined,
    pointerEvents: readOnly ? 'none' : undefined,
  });

const boxStyle =
  (isDragOver: boolean, isMobile: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    border: `1px solid ${isDragOver ? backrs.colors['Brand/600'] : isMobile ? backrs.colors['Colors/Border/border-secondary'] : backrs.colors['Beige/950']}`,
    borderRadius: isMobile ? backrs.radius['radius-xl'] : backrs.radius['radius-md'],
    flex: 1,
    pl: isMobile ? backrs.spacing['spacing-lg'] : backrs.spacing['spacing-md'],
    pr: isMobile ? backrs.spacing['spacing-lg'] : backrs.spacing['spacing-md'],
    minHeight: isMobile ? '56px' : undefined,
    backgroundColor: isDragOver ? backrs.colors['Brand/50'] : 'transparent',
    transition: 'border-color 0.2s, background-color 0.2s',
  });

const textareaRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
};

const textareaWrapperSx: SxProps<Theme> = {
  position: 'relative',
  flex: 1,
  minWidth: 0,
};

const attachedNuggetsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-sm'],
  pt: backrs.spacing['spacing-md'],
  width: '100%',
});

const inputTextSx = (isMobile: boolean) => ({
  fontSize: '1rem',
  lineHeight: '1.5rem',
  padding: isMobile ? '14px 4px' : '12px 8px',
});

const messageInputTextarea = (isMobile: boolean): TextareaAutosizeProps['style'] => ({
  ...inputTextSx(isMobile),
  width: '100%',
  resize: 'none',
  border: 'none',
  outline: 'none',
  fontFamily: 'inherit',
  overflowY: 'auto',
  letterSpacing: '0',
  fontWeight: '400',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'transparent',
});

const placeholderOverlaySx =
  (isMobile: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    ...inputTextSx(isMobile),
    position: 'absolute',
    inset: 0,
    color: backrs.colors['Colors/Text/text-placeholder'],
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  });

const iconButtonStyle: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-md'],
});

const stopButtonSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const mobileButtonsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const bottomRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  pb: backrs.spacing['spacing-sm'],
});

const attachButtonSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-xs'],
  flexShrink: 0,
});

const recordButtonSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-xs'],
  flexShrink: 0,
  color: backrs.colors['Colors/Foreground/fg-quaternary'],
});
